import { Button, Modal, ModalDescription, ModalFooter, ModalHeader, type ModalProps, ModalTitle, SwitchThemeContext } from '@tryhackme/thm-ui-components';
import { type ReactNode, useContext, useMemo, useState } from 'react';
import { CHILI_PIPER_SUBDOMAIN, ChiliPiperRouterNames } from 'src/common/constants';
import { ThemeColorMode } from 'src/common/enums';
import { useChiliPiperJS } from 'src/common/hooks/use-chilipiper-js';
import { useCountryCode } from 'src/common/hooks/use-country-code';
import { useExternalScript } from 'src/common/hooks/use-external-script';
import { HubspotForm } from 'src/common/hubspot-form';
import type { HubspotFormProps } from 'src/common/hubspot-form/hubspot-form.types';
import type { UserObjectProps } from 'src/common/interfaces/tracking/segment';
import { useGetUserQuery } from 'src/common/slices';
import { useTrackFormSubmitMutation } from 'src/common/slices/analytics';
import { anonymousId } from 'src/common/tracking/data';
import { hsCss } from './book-now-modal.constants';
import { RecaptchaGlobalStyles, StyledDescription, StyledFooterWrapper, StyledFormWrapper, StyledWrapper } from './book-now-modal.styles';
export type BookNowModalProps = ModalProps & {
  title?: string;
  description?: string | ReactNode;
  forceDarkMode?: boolean;
  onFormSubmitted?: HubspotFormProps['onFormSubmitted'];
};
export const BookNowModal = ({
  title = 'Book a demo',
  description,
  onOpenChange,
  forceDarkMode,
  ...props
}: BookNowModalProps) => {
  const {
    theme: themeName
  } = useContext(SwitchThemeContext);
  const isDarkMode = forceDarkMode ?? themeName === ThemeColorMode.DARK;
  const {
    chiliPiperScript,
    isLoading: isLoadingChiliPiper
  } = useChiliPiperJS();
  const isLoadingHubspot = useExternalScript('https://js.hsforms.net/forms/v2.js');
  const [trackFormSubmit] = useTrackFormSubmitMutation();
  const {
    data: user,
    isSuccess
  } = useGetUserQuery();
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const {
    countryCode
  } = useCountryCode(user?.data?.user);
  const userObject: UserObjectProps = useMemo(() => isSuccess && user?.data?.user?._id ? {
    userId: user.data.user._id
  } : {
    anonymousId
  }, [isSuccess, user]);
  const handleContactFormReady = () => {
    const iframe = document.querySelector<HTMLIFrameElement>('#hs-form-iframe-1');
    if (iframe) {
      const input = iframe.contentWindow?.document.querySelector<HTMLInputElement>('input[name="cp_country_code"]');
      if (input) {
        input.value = countryCode;
      }
    }
  };
  const handleContactFormSubmit: HubspotFormProps['onFormSubmitted'] = (_, data) => {
    const {
      'g-recaptcha-response': captcha,
      hs_context: hs,
      ...rest
    } = data.submissionValues;
    const lead = {
      ...rest,
      cp_country_code: countryCode
    };
    chiliPiperScript?.submit(CHILI_PIPER_SUBDOMAIN, ChiliPiperRouterNames.B2B, {
      map: true,
      lead
    });
    setIsFormSubmitted(true);
    trackFormSubmit({
      form_type: 'business-form-submit',
      userObject
    }).catch(() => {
      // silently discard errors. We don't mind if tracking events aren't fired
    });
  };
  const isLoadingExternalScripts = isLoadingChiliPiper || isLoadingHubspot;
  const handleOpenChange = (state: boolean) => {
    setIsFormSubmitted(false);
    onOpenChange?.(state);
  };
  return <Modal {...props} onOpenChange={handleOpenChange} preventCloseOnOutsideClick data-sentry-element="Modal" data-sentry-component="BookNowModal" data-sentry-source-file="book-now-modal.tsx">
      <RecaptchaGlobalStyles data-sentry-element="RecaptchaGlobalStyles" data-sentry-source-file="book-now-modal.tsx" />
      <ModalHeader isSmall data-sentry-element="ModalHeader" data-sentry-source-file="book-now-modal.tsx">
        <ModalTitle data-sentry-element="ModalTitle" data-sentry-source-file="book-now-modal.tsx">{title}</ModalTitle>
      </ModalHeader>

      {description ? <ModalDescription>
          <StyledDescription $isDarkMode={isDarkMode}>{description}</StyledDescription>
        </ModalDescription> : null}

      <StyledWrapper data-sentry-element="StyledWrapper" data-sentry-source-file="book-now-modal.tsx">
        {isLoadingExternalScripts ? <p>Loading...</p> : <StyledFormWrapper>
            <HubspotForm region="na1" portalId="7168674" formId="ad32adac-6cb1-4254-a857-abe624a530a7" onFormSubmitted={handleContactFormSubmit} onFormReady={handleContactFormReady} submitText="Submit details" css={hsCss(isDarkMode)} />
          </StyledFormWrapper>}

        <StyledFooterWrapper $isFormSubmitted={isFormSubmitted} $isDarkMode={isDarkMode} data-sentry-element="StyledFooterWrapper" data-sentry-source-file="book-now-modal.tsx">
          <ModalFooter isSmall data-sentry-element="ModalFooter" data-sentry-source-file="book-now-modal.tsx">
            <Button variant="outlined" color="secondary" onClick={() => handleOpenChange(false)} data-sentry-element="Button" data-sentry-source-file="book-now-modal.tsx">
              Close
            </Button>
          </ModalFooter>
        </StyledFooterWrapper>
      </StyledWrapper>
    </Modal>;
};